/**
 * ⌘K command palette — fuzzy search across every route + a few action verbs.
 *
 * Triggered from the header search input or anywhere via cmd/ctrl+K (handled
 * in Shell.tsx). Listens for arrow keys + enter while open. Closes on Escape
 * or backdrop click.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Command {
  id: string;
  label: string;
  hint: string;
  icon: string;
  /** kind drives grouping in the result list */
  kind: 'page' | 'action';
  /** what to do when selected — return false to keep the palette open */
  run: (nav: ReturnType<typeof useNavigate>) => void | boolean;
}

/** The catalog of jump-to-able things. Add a row → it's instantly searchable. */
const COMMANDS: Command[] = [
  // Pages
  { id: 'dashboard',   label: 'Dashboard',         hint: 'Workspace overview',           icon: '◫', kind: 'page', run: (n) => n('/app/dashboard') },
  { id: 'inbox',       label: 'Inbox',             hint: 'Replies & DMs',                icon: '✉', kind: 'page', run: (n) => n('/app/inbox') },
  { id: 'analytics',   label: 'Analytics',         hint: 'Engagement & top posts',       icon: '◔', kind: 'page', run: (n) => n('/app/analytics') },
  { id: 'content',     label: 'Content library',   hint: 'Items mined from sources',     icon: '◰', kind: 'page', run: (n) => n('/app/content') },
  { id: 'sources',     label: 'Sources',           hint: 'RSS feeds & keywords',         icon: '⟲', kind: 'page', run: (n) => n('/app/sources') },
  { id: 'templates',   label: 'Templates',         hint: 'Caption skeletons',            icon: '⬚', kind: 'page', run: (n) => n('/app/templates') },
  { id: 'media',       label: 'Media library',     hint: 'Reusable images',              icon: '◉', kind: 'page', run: (n) => n('/app/media') },
  { id: 'brand',       label: 'Brand kit',         hint: 'Voice, audience, pillars',     icon: '◬', kind: 'page', run: (n) => n('/app/brand') },
  { id: 'create',      label: 'Create posts',      hint: 'Compose a new post',           icon: '✦', kind: 'page', run: (n) => n('/app/create') },
  { id: 'drafts',      label: 'Drafts',            hint: 'Posts awaiting publish',       icon: '◳', kind: 'page', run: (n) => n('/app/drafts') },
  { id: 'calendar',    label: 'Calendar',          hint: 'Scheduled posts by date',      icon: '▦', kind: 'page', run: (n) => n('/app/calendar') },
  { id: 'automation',  label: 'Automation',        hint: 'Hands-free publishing rules',  icon: '⚡', kind: 'page', run: (n) => n('/app/automation') },
  { id: 'connections', label: 'Connections',       hint: 'Link social accounts',         icon: '⬡', kind: 'page', run: (n) => n('/app/connections') },
  { id: 'integrations',label: 'Integrations',      hint: 'Slack / webhooks / Zapier',    icon: '⊕', kind: 'page', run: (n) => n('/app/integrations') },
  { id: 'billing',     label: 'Billing',           hint: 'Plan, credits, top-ups',       icon: '◊', kind: 'page', run: (n) => n('/app/billing') },
  { id: 'settings',    label: 'Settings',          hint: 'Workspace + account',          icon: '⚙', kind: 'page', run: (n) => n('/app/settings') },
  { id: 'profile',     label: 'Profile',           hint: 'You + your workspaces',        icon: '◉', kind: 'page', run: (n) => n('/app/profile') },
  // Actions
  { id: 'new-post',    label: 'New post',          hint: 'Open composer',                icon: '+', kind: 'action', run: (n) => n('/app/create') },
  { id: 'new-rule',    label: 'New automation',    hint: 'Hands-free publishing',        icon: '⚡', kind: 'action', run: (n) => n('/app/automation') },
  { id: 'connect',     label: 'Connect a platform',hint: 'Link a social account',        icon: '⬡', kind: 'action', run: (n) => n('/app/connections') },
];

/** Simple inclusion-based scoring — order matches first, then any-substring. */
function score(needle: string, hay: string): number {
  if (!needle) return 0;
  const n = needle.toLowerCase();
  const h = hay.toLowerCase();
  if (h.startsWith(n)) return 100 - (h.length - n.length);
  if (h.includes(n)) return 50;
  return -1;
}

interface PaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: PaletteProps) {
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input + reset state every time it opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return COMMANDS;
    return COMMANDS
      .map((c) => ({ c, s: Math.max(score(query, c.label), score(query, c.hint), score(query, c.id)) }))
      .filter((r) => r.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((r) => r.c);
  }, [query]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      const c = filtered[highlight];
      if (c) { c.run(nav); onClose(); }
    }
    else if (e.key === 'Escape') onClose();
  };

  if (!open) return null;

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-stone-900/40">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100">
          <span className="text-stone-400">⌕</span>
          <input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={onKey} placeholder="Search pages, actions, drafts…"
            className="flex-1 outline-none text-sm placeholder:text-stone-400" />
          <kbd className="text-[10px] text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">esc</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-stone-400 py-8">No matches.</p>
          ) : (
            filtered.map((c, i) => {
              const active = i === highlight;
              return (
                <button key={c.id}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => { c.run(nav); onClose(); }}
                  className={`w-full px-4 py-2.5 flex items-center gap-3 text-left ${active ? 'bg-violet-50' : 'hover:bg-stone-50'}`}>
                  <span className={`w-7 h-7 rounded-md flex items-center justify-center text-sm ${active ? 'bg-violet-100 text-violet-700' : 'bg-stone-100 text-stone-600'}`}>{c.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{c.label}</p>
                    <p className="text-[11px] text-stone-500 truncate">{c.hint}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${c.kind === 'action' ? 'bg-amber-50 text-amber-700' : 'bg-stone-100 text-stone-500'}`}>
                    {c.kind}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="px-4 py-2 border-t border-stone-100 flex items-center gap-3 text-[10px] text-stone-400">
          <span><kbd className="bg-stone-100 px-1.5 py-0.5 rounded">↑↓</kbd> navigate</span>
          <span><kbd className="bg-stone-100 px-1.5 py-0.5 rounded">↵</kbd> open</span>
          <span><kbd className="bg-stone-100 px-1.5 py-0.5 rounded">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
